import React from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import type { CtraderOpenApiSyncInfo } from "../../utils/ctraderOpenApiSync";
import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
} from "./brokerAlertUi";
import CtraderCsvStatementColumnGuidance from "./CtraderCsvStatementColumnGuidance";

export interface CtraderOpenApiSyncGuidanceProps {
  openApiSync: CtraderOpenApiSyncInfo;
  emphasizeCsv?: boolean;
  onGoToCsvImport?: () => void;
  compact?: boolean;
  /** Rolling API window in days, when known from broker capabilities. */
  lookbackDays?: number | null;
}

const CtraderOpenApiSyncGuidance = ({
  openApiSync,
  emphasizeCsv = false,
  onGoToCsvImport,
  lookbackDays,
}: CtraderOpenApiSyncGuidanceProps): React.ReactElement => {
  const lookbackLabel =
    typeof lookbackDays === "number" && lookbackDays > 0
      ? `about the last ${lookbackDays} days`
      : "a limited window";

  return (
    <Alert variant="info" className={BROKER_ALERT_CLASS}>
      <BrokerAlertIcon tone="info" />
      <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>How cTrader sync works</AlertTitle>
      <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
        <p>
          Connect your cTrader account once and Tessera will keep your trades up to date. You sign
          in with OAuth, choose which account to sync, and we import your deal history from cTrader.
        </p>
        <div className="mt-3 flex flex-col gap-5">
          <p>
            <span className={BROKER_ALERT_TITLE_CLASS}>Sign in with OAuth.</span> Tessera requests
            read-only access to your accounts. Pick the account you trade on by its login number
            (for example, 5826993). We map that to your cTrader account ID for syncing.
          </p>
          <p>
            <span className={BROKER_ALERT_TITLE_CLASS}>Allow time on the first sync.</span> Deal
            history is imported when you sync and can take several minutes the first time. Leave
            this tab open until it finishes.
          </p>
          {openApiSync.historyBeforeSyncRequiresCsvImport ? (
            <p>
              <span className={BROKER_ALERT_TITLE_CLASS}>Older trades need a CSV.</span> Open API
              sync only covers {lookbackLabel}. Upload a cTrader statement export for history before
              that.
            </p>
          ) : null}
          <CtraderCsvStatementColumnGuidance className="!text-inherit" />
        </div>
        {emphasizeCsv && onGoToCsvImport ? (
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            className="mt-3"
            onClick={onGoToCsvImport}
          >
            Go to CSV import
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
};

export default CtraderOpenApiSyncGuidance;
